import { Injectable } from '@angular/core';
import {from, Observable, throwError, timer} from "rxjs";
import {catchError, map, mergeMap, retryWhen, switchMap, tap} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {HttpClient, HttpContext, HttpErrorResponse, HttpEventType, HttpHeaders} from "@angular/common/http";
import {plainToClass, plainToClassFromExist} from "class-transformer";
import {MediaObjectApiModel} from "../models/media-object.api-model";
import {ResultListApiModel} from "../models/result-list.api.model";
import {QueryParamsApiModel} from "../models/query-params-api.model";
import {QueryParamsService} from "../../core/services/query-params.service";
import {MediaTypeEnum} from "../../core/enums/media-type.enum";
import {SKIP_CANCEL_ON_NAV} from "../../core/providers/http-context-tokens";
import {UploadPreprocessService} from "../../core/services/upload-preprocess.service";
import {BackgroundUploadOrchestratorService} from "../../core/services/background-upload-orchestrator.service";

@Injectable({
  providedIn: 'root'
})
export class MediaObjectRestService {

  private readonly resource: string = '/media_objects';
  private readonly uploadRetryDelaysMs = [1000, 3000, 7000];
  totalItems: number = 0;

  constructor(
    private http: HttpClient,
    private params: QueryParamsService,
    private readonly uploadPreprocess: UploadPreprocessService,
    private readonly backgroundUploadOrchestrator: BackgroundUploadOrchestratorService,
  ) { }

  getMediaObject(id: string, options?: {headers?: HttpHeaders}): Observable<MediaObjectApiModel> {
    const context = new HttpContext().set(SKIP_CANCEL_ON_NAV, true);
    return this.http.get<MediaObjectApiModel>(this.resolveMediaObjectUrl(id), {...options, context})
      .pipe(map((response: MediaObjectApiModel) => {
        return plainToClass(MediaObjectApiModel, response);
    }));
  }

  getMediaObjectCollection(queryParamsApiModel?: QueryParamsApiModel, options?: {headers?: HttpHeaders}) {
    const params = this.params.applyParameters(queryParamsApiModel);
    const context = new HttpContext().set(SKIP_CANCEL_ON_NAV, true);
    return this.http.get<ResultListApiModel<MediaObjectApiModel>>(environment.apiUrl + this.resource, {...options, params, context})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<MediaObjectApiModel>(MediaObjectApiModel), result as object)));
  }

  deleteMediaObject(id: string) {
    return this.http.delete(environment.apiUrl + id)
  }

  upload(file: FormData): Observable<MediaObjectApiModel> {
    return this.withInMemoryUploadRetry(() => this.http.post<MediaObjectApiModel>(environment.apiUrl + this.resource, file));
  }

  uploadImage(image: File, caption: string = null, filterPreset?: string | null): Observable<MediaObjectApiModel> {
    return from(this.uploadPreprocess.prepare(image, 'image', 'media_objects.image')).pipe(
      switchMap((preparedUpload) => {
        const fd = MediaObjectRestService.prepareFormData(preparedUpload.file, MediaTypeEnum.IMAGE, caption, undefined, undefined, filterPreset);
        void this.backgroundUploadOrchestrator.enqueue(preparedUpload, this.resource);
        void this.backgroundUploadOrchestrator.markUploading(preparedUpload.clientUploadId);
        return this.upload(fd).pipe(
          map((response: MediaObjectApiModel) => {
            const parsed = plainToClass(MediaObjectApiModel, response);
            void this.backgroundUploadOrchestrator.markUploaded(preparedUpload.clientUploadId, parsed['@id']);
            return parsed;
          }),
          tap(() => this.uploadPreprocess.markUploaded(preparedUpload)),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            void this.backgroundUploadOrchestrator.markFailed(preparedUpload.clientUploadId, error?.message || 'Upload failed');
            return throwError(() => error);
          })
        );
      })
    );
  }

  uploadMedia(type: MediaTypeEnum, image: File, caption: string = null, thumbnail?: File, thumbnailTime?: number, filterPreset?: string | null): Observable<number | MediaObjectApiModel> {
    const category = type === MediaTypeEnum.VIDEO ? 'video' : 'image';
    return from(this.uploadPreprocess.prepare(image, category, 'media_objects.progress')).pipe(
      switchMap((preparedUpload) => {
        const fd = MediaObjectRestService.prepareFormData(preparedUpload.file, type, caption, thumbnail, thumbnailTime, filterPreset);
        void this.backgroundUploadOrchestrator.enqueue(preparedUpload, this.resource);
        void this.backgroundUploadOrchestrator.markUploading(preparedUpload.clientUploadId);
        return this.withInMemoryUploadRetry(() => this.http.post<MediaObjectApiModel>(environment.apiUrl + this.resource, fd, {
          reportProgress: true,
          observe: 'events',
        })).pipe(
          map(event => {
            if (event.type === HttpEventType.UploadProgress) {
              // Emituj procentowy postęp
              return Math.round((event.loaded / (event.total || 1)) * 100);
            } else if (event.type === HttpEventType.Response) {
              // Po zakończeniu zwróć model
              this.uploadPreprocess.markUploaded(preparedUpload);
              const parsed = plainToClass(MediaObjectApiModel, event.body);
              void this.backgroundUploadOrchestrator.markUploaded(preparedUpload.clientUploadId, parsed['@id']);
              return parsed;
            }
            return 0; // Wartość domyślna
          }),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            void this.backgroundUploadOrchestrator.markFailed(preparedUpload.clientUploadId, error?.message || 'Upload failed');
            return throwError(() => error);
          })
        );
      })
    );
  }

  uploadVideo(image: File, caption: string = null, thumbnail?: File, thumbnailTime?: number, filterPreset?: string | null): Observable<MediaObjectApiModel> {
    console.info('[MediaObjectRS] uploadVideo start', {
      size: image?.size,
      type: image?.type,
      name: image?.name,
      hasThumbnail: !!thumbnail,
      thumbnailTime,
      filterPreset,
    });
    return from(this.uploadPreprocess.prepare(image, 'video', 'media_objects.video')).pipe(
      switchMap((preparedUpload) => {
        const fd = MediaObjectRestService.prepareFormData(preparedUpload.file, MediaTypeEnum.VIDEO, caption, thumbnail, thumbnailTime, filterPreset);
        void this.backgroundUploadOrchestrator.enqueue(preparedUpload, this.resource);
        void this.backgroundUploadOrchestrator.markUploading(preparedUpload.clientUploadId);
        return this.upload(fd).pipe(
          map((response: MediaObjectApiModel) => {
            const parsed = plainToClass(MediaObjectApiModel, response);
            void this.backgroundUploadOrchestrator.markUploaded(preparedUpload.clientUploadId, parsed['@id']);
            return parsed;
          }),
          tap(() => this.uploadPreprocess.markUploaded(preparedUpload)),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            void this.backgroundUploadOrchestrator.markFailed(preparedUpload.clientUploadId, error?.message || 'Upload failed');
            return throwError(() => error);
          })
        );
      })
    );
  }

  private static prepareFormData(file: File, type: any, caption: string, thumbnail?: File, thumbnailTime?: number, filterPreset?: string | null): FormData {
    const fd = new FormData();
    fd.append('file', file, file.name);
    fd.append('type', type);
    fd.append('caption', caption);
    if (thumbnail) {
      fd.append('thumbnail', thumbnail, thumbnail.name);
    }
    if (thumbnailTime !== undefined && thumbnailTime !== null) {
      fd.append('thumbnailTime', `${thumbnailTime}`);
    }
    if (filterPreset) {
      fd.append('filterPreset', filterPreset);
    }
    return fd;
  }

  getMediaObjectSubresourceCollection(ownerResource: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()) {
    const params = this.params.applyParameters(queryParamsApiModel);
    const context = new HttpContext().set(SKIP_CANCEL_ON_NAV, true);
    return this.http.get<ResultListApiModel<MediaObjectApiModel>>(environment.apiUrl + '/' + ownerResource  + this.resource, {params, context})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<MediaObjectApiModel>(MediaObjectApiModel), result as object)));
  }

  private resolveMediaObjectUrl(reference: string): string {
    if (!reference) {
      return environment.apiUrl + this.resource;
    }

    if (reference.startsWith('http://') || reference.startsWith('https://')) {
      return reference;
    }

    if (reference.startsWith('/api/')) {
      const apiOrigin = new URL(environment.apiUrl).origin;
      return `${apiOrigin}${reference}`;
    }

    if (reference.startsWith('/')) {
      return `${environment.apiUrl}${reference}`;
    }

    return `${environment.apiUrl}/${reference}`;
  }

  private withInMemoryUploadRetry<T>(requestFactory: () => Observable<T>): Observable<T> {
    return requestFactory().pipe(
      retryWhen((errors) => errors.pipe(
        mergeMap((error, index) => {
          const attempt = index + 1;
          if (!this.isRetryableUploadError(error) || attempt > this.uploadRetryDelaysMs.length) {
            return throwError(() => error);
          }

          const retryDelayMs = this.uploadRetryDelaysMs[attempt - 1];
          console.info('[MediaObjectRS] upload retry scheduled', {
            attempt,
            retryDelayMs,
            status: error?.status ?? null,
            message: error?.message ?? null,
          });
          return timer(retryDelayMs);
        })
      ))
    );
  }

  private isRetryableUploadError(error: unknown): boolean {
    const httpError = error as HttpErrorResponse | undefined;
    const status = Number(httpError?.status || 0);
    const message = `${httpError?.message || ''} ${httpError?.statusText || ''}`.toLowerCase();

    // Invalid certificate / TLS errors are not transient retries from app perspective.
    if (message.includes('err_cert')) {
      return false;
    }

    if (status === 0) {
      return true;
    }

    if ([408, 425, 429].includes(status)) {
      return true;
    }

    return false;
  }
}
